import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { buttonVariants } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';
import { LanguageSelector } from '@/components/core';
import { useAuthState } from '@/state/client-middleware';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui-kit/sheet';

const navItems = [
  { key: 'LANDING_NAV_TEMPLATES', href: '/templates' },
  { key: 'LANDING_NAV_CONTACT', href: 'mailto:hello@selise.ch' },
];

export const LandingHeader = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthState();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200/80 bg-white shadow-sm">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src="/logo.svg"
            alt="SELISE Legal Templates"
            className="h-9 w-auto md:h-10"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navItems.map(({ key, href }) => {
            const isInternal = href.startsWith('/');
            const isActive = isInternal && pathname === href;
            const classes = `text-sm font-semibold font-bahnschrift transition-colors focus-visible:text-selise-blue focus-visible:underline focus-visible:outline-none ${
              isActive
                ? 'text-selise-blue'
                : 'text-eerie-black hover:text-selise-blue'
            }`;

            return isInternal ? (
              <Link
                key={key}
                to={href}
                className={classes}
                {...(isActive ? { 'aria-current': 'page' as const } : {})}
              >
                {t(key)}
              </Link>
            ) : (
              <a key={key} href={href} className={classes}>
                {t(key)}
              </a>
            );
          })}
        </nav>

        {/* Desktop Right */}
        <div className="hidden md:flex items-center gap-4">
          <LanguageSelector variant="landing" />
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className={cn(buttonVariants({ size: 'sm' }), 'bg-oxford-blue hover:bg-oxford-blue/90 text-white rounded-full px-6')}
            >
              {t('LANDING_NAV_DASHBOARD')}
            </Link>
          ) : (
            <a
              href={`${import.meta.env.VITE_TEMPLATE_APP_BASE_URL}/login`}
              className={cn(buttonVariants({ size: 'sm' }), 'bg-oxford-blue hover:bg-oxford-blue/90 text-white rounded-full px-6')}
            >
              {t('LANDING_SIGN_IN')}
            </a>
          )}
        </div>

        {/* Mobile Menu */}
        <div className="flex md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md min-h-[44px] min-w-[44px] text-eerie-black hover:bg-gray-100"
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-sm p-0">
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-gray-200">
                <SheetTitle className="sr-only">{t('LANDING_NAV_MENU')}</SheetTitle>
                <img
                  src="/logo.svg"
                  alt="SELISE Legal Templates"
                  className="h-8 w-auto"
                />
              </SheetHeader>

              <div className="flex-1 overflow-y-auto px-6 py-6">
                <nav className="space-y-1">
                  {navItems.map(({ key, href }) => {
                    const isInternal = href.startsWith('/');
                    const isActive = isInternal && pathname === href;
                    const classes = `block rounded-lg px-4 py-3 text-base font-semibold font-bahnschrift transition-colors ${
                      isActive
                        ? 'text-selise-blue bg-selise-blue/5'
                        : 'text-eerie-black hover:bg-gray-50'
                    }`;

                    return isInternal ? (
                      <Link
                        key={key}
                        to={href}
                        onClick={() => setMobileOpen(false)}
                        className={classes}
                        {...(isActive ? { 'aria-current': 'page' as const } : {})}
                      >
                        {t(key)}
                      </Link>
                    ) : (
                      <a
                        key={key}
                        href={href}
                        onClick={() => setMobileOpen(false)}
                        className={classes}
                      >
                        {t(key)}
                      </a>
                    );
                  })}
                </nav>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <LanguageSelector variant="landing" />
                </div>

                <div className="mt-4">
                  {isAuthenticated ? (
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-center rounded-xl px-4 py-3 text-base font-semibold text-white bg-oxford-blue hover:bg-oxford-blue/90 transition-colors"
                    >
                      {t('LANDING_NAV_DASHBOARD')}
                    </Link>
                  ) : (
                    <a
                      href={`${import.meta.env.VITE_TEMPLATE_APP_BASE_URL}/login`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-center rounded-xl px-4 py-3 text-base font-semibold text-white bg-oxford-blue hover:bg-oxford-blue/90 transition-colors"
                    >
                      {t('LANDING_SIGN_IN')}
                    </a>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
